/**
 * Layer 2: the module-level handle registry.
 *
 * Holds the non-serializable bits keyed by queue id — the live `File` and the two cancel
 * handles the existing upload transport already exposes. Deliberately outside Jotai: these
 * are imperative handles, not render state, and putting a `File` in an atom would make the
 * queue unserializable and re-render rows for no reason.
 *
 * Only `actions.ts` touches this, so the atoms and the handles can never drift apart.
 */
import type { SupplyAbortSignals } from '@studio/api/library';
import { CANCEL_REASON } from '@studio/constants/library-upload';
import type { ChecksumTask } from '@studio/pages/Library/types/upload-video';

type Handles = {
  /** `.abort()` cancels an in-flight upload — supplied by `uploadFile`'s `setSource` */
  abort?: SupplyAbortSignals;
  /** `.cancel()` aborts the checksum worker — returned by `computeChecksum` */
  checksumTask?: ChecksumTask;
  file: File;
};

const handles = new Map<string, Handles>();

/** Abort whichever handle is live for the item's current phase. Safe to call at any time. */
const abortHandles = (id: string) => {
  const entry = handles.get(id);
  entry?.checksumTask?.cancel();
  entry?.abort?.abort(CANCEL_REASON);
};

export const uploadRegistry = {
  add: (id: string, file: File) => {
    handles.set(id, { file });
  },
  cancel: abortHandles,
  /** Abort and release everything — used on the session boundary (org switch / login). */
  clear: () => {
    [...handles.keys()].forEach(abortHandles);
    handles.clear();
  },
  /** Release the `File` so a large upload does not pin memory once it can no longer be retried. */
  drop: (id: string) => {
    handles.delete(id);
  },
  file: (id: string): File | undefined => handles.get(id)?.file,
  has: (id: string): boolean => handles.has(id),
  setAbort: (id: string, abort: SupplyAbortSignals) => {
    const entry = handles.get(id);
    if (entry) entry.abort = abort;
  },
  setChecksumTask: (id: string, checksumTask: ChecksumTask) => {
    const entry = handles.get(id);
    if (entry) entry.checksumTask = checksumTask;
  },
};
